import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Error as ErrorIcon } from "@mui/icons-material";
import { Charge } from "@/pages/sy/models/Charge";

export interface ErrorDetail {
  label: string;
  value: string | number | null | undefined;
}

interface ErrorDetailsModalProps {
  open: boolean;
  title: string;
  message: string;
  details?: ErrorDetail[];
  detailsTitle?: string;
  guidanceMessage?: string;
  onClose: () => void;
  // Bucket conflict specific props
  isBucketConflict?: boolean;
  conflictingCharge?: Charge | null;
  selectedCharge?: Charge | null;
  onContinueWithConflictingCharge?: () => void;
  onContinueWithSelectedCharge?: () => void;
}

export const ErrorDetailsModal = ({
  open,
  title,
  message,
  details,
  detailsTitle = "Error Details:",
  guidanceMessage,
  onClose,
  isBucketConflict = false,
  conflictingCharge,
  selectedCharge,
  onContinueWithConflictingCharge,
  onContinueWithSelectedCharge,
}: ErrorDetailsModalProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <ErrorIcon sx={{ color: "error.main", fontSize: "3rem", mr: 1.5 }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, fontSize: "1.75rem" }}
          >
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph sx={{ fontSize: "1.25rem" }}>
          {message}
        </Typography>

        {details && details.length > 0 ? (
          <Box p={2} mb={2}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "1.1rem" }}
            >
              {detailsTitle}
            </Typography>
            {details.map((detail, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{ fontSize: "1.1rem" }}
              >
                • {detail.label}: {detail.value ?? "N/A"}
              </Typography>
            ))}
          </Box>
        ) : null}

        {guidanceMessage ? (
          <Typography variant="body1" paragraph sx={{ fontSize: "1.25rem" }}>
            {guidanceMessage}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          padding: "8px 24px 16px",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {isBucketConflict &&
        conflictingCharge &&
        selectedCharge &&
        onContinueWithConflictingCharge &&
        onContinueWithSelectedCharge ? (
          <Box display="flex" width="100%" gap={2}>
            <Box display="flex" flexDirection="column" flex={1} gap={0.5}>
              <Button
                onClick={onContinueWithConflictingCharge}
                variant="contained"
                color="success"
                fullWidth
                sx={{
                  fontSize: "1rem",
                  padding: "24px 16px",
                }}
              >
                Continue with {conflictingCharge.ScheduledHeatName || "N/A"} #
                {conflictingCharge.ChargeNumber || 1}
              </Button>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: "1rem",
                }}
              >
                {selectedCharge.ScheduledHeatName || "N/A"} #
                {selectedCharge.ChargeNumber || 1} will continue as Scheduled
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" flex={1} gap={0.5}>
              <Button
                onClick={onContinueWithSelectedCharge}
                variant="contained"
                color="warning"
                fullWidth
                sx={{
                  fontSize: "1rem",
                  padding: "24px 16px",
                }}
              >
                Continue with {selectedCharge.ScheduledHeatName || "N/A"} #
                {selectedCharge.ChargeNumber || 1}
              </Button>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: "1rem",
                }}
              >
                {conflictingCharge.ScheduledHeatName || "N/A"} #
                {conflictingCharge.ChargeNumber || 1} will be set Charged Into
                EAF
              </Typography>
            </Box>
          </Box>
        ) : null}
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ minWidth: 140, fontSize: "1.1rem", padding: "24px 24px" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
